import React, { useEffect, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../Components/Loader/Loader";
import HTMLParser from "../../../Components/HTMLParser";
import Timer from "../../Components/Timer";
import UBTQuestion from "../UBTPages/UBTQuestion";
import {
  clearAllValue,
  setSaveResult,
} from "../../../../Features/SetsAPI/LocalSetSlice";
import {
  errorMessage,
  getSetQuestions,
  submitSetResult,
} from "../../../../Features/LKWBTestApi/LKWBTestApi";

// Backend limit for each short-answer (writing) response.
const WRITING_MAX_LENGTH = 50;

const isWriting = (q) => q?.answers?.type === "writing";
const sectionOf = (q) => q?.scoring?.section || (isWriting(q) ? "writing" : "objective");
const marksOf = (q) => Number(q?.scoring?.marks) || 0;

const SECTION_LABELS = {
  objective: "Objective Questions",
  writing: "Writing Questions",
};
const sectionLabel = (key) =>
  SECTION_LABELS[key] ||
  `${key.charAt(0).toUpperCase()}${key.slice(1)} Questions`;

// Short-answer question: same layout as UBTQuestion, but with a text box.
const WritingQuestion = ({ question, questionNo, value, onChange }) => (
  <div className="questions">
    <h4>{question.groupTitle}</h4>
    <div className="d-flex questionGroups">
      <span>{questionNo}.</span>
      <div className="flex-grow-1">
        {question.mainTitle && (
          <h4>
            <HTMLParser>{question.mainTitle}</HTMLParser>
          </h4>
        )}
        {question.subTitle && (
          <h5>
            <HTMLParser>{question.subTitle}</HTMLParser>
          </h5>
        )}
        {question.type === "image" && question.file && (
          <div className="image-container">
            <img src={question.file} alt="" />
          </div>
        )}
        <textarea
          className="form-control mt-3"
          rows={3}
          placeholder="Write your answer here"
          maxLength={WRITING_MAX_LENGTH}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, WRITING_MAX_LENGTH))}
        />
        <div
          className={`lkwb-char-count ${
            value.length >= WRITING_MAX_LENGTH ? "limit" : ""
          }`}
        >
          {value.length >= WRITING_MAX_LENGTH &&
            "Maximum characters reached. "}
          {value.length} / {WRITING_MAX_LENGTH}
        </div>
      </div>
    </div>
  </div>
);

// Sidebar group of question-number links, styled like the UBT test sidebar.
const QuestionGroup = ({ eventId, label, items, current, isAnswered, onSelect }) => (
  <Accordion.Item eventKey={eventId}>
    <Accordion.Header>
      {label} {items.length}
    </Accordion.Header>
    <Accordion.Body>
      <div className="questions-container">
        <ul className="questions-items">
          {items.map(({ question, index }) => (
            <li key={question.id}>
              <a
                href="#/"
                className={`${index === current ? "active" : ""} ${
                  isAnswered(question) ? "attempted" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(index);
                }}
              >
                {question.question_number ?? index + 1}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Accordion.Body>
  </Accordion.Item>
);

const LKWBTestQuestions = () => {
  const { id, setId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((s) => s.auth.currentUser.token);
  const solved = useSelector((s) => s.localset.solvedQuestion);

  const backLink = `/dashboard/lkwb-test/${id}`;
  const [set, setSet] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [writingAnswers, setWritingAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Start from a clean slate so answers from another test don't leak in.
    dispatch(clearAllValue());
    localStorage.removeItem("testTime");
    setIsLoading(true);
    getSetQuestions(id, setId, token)
      .then((data) => {
        if (cancelled) return;
        const list = (data?.questions || [])
          .filter((q) => q && (isWriting(q) || Array.isArray(q.answers?.options)))
          .sort(
            (a, b) => (a.question_number ?? 0) - (b.question_number ?? 0)
          );
        setSet(data?.set || null);
        setStartedAt(Date.now());
        setQuestions(list);
        setCurrent(0);
        setError(list.length === 0 ? "No Questions" : "");
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, "Unable to load questions."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
      dispatch(clearAllValue());
      localStorage.removeItem("testTime");
    };
  }, [dispatch, id, setId, token]);

  const selectedOption = (questionId) =>
    solved
      .find((q) => q.id === questionId)
      ?.answers?.options?.find((o) => o.userSelected === true);

  const isAnswered = (q) =>
    isWriting(q)
      ? !!writingAnswers[q.id]?.trim()
      : selectedOption(q.id) !== undefined;

  const buildPayload = () => {
    const objective = questions.filter((q) => !isWriting(q));
    const correct = objective.filter(
      (q) => selectedOption(q.id)?.is_correct === 1
    );
    const attempted = questions.filter(isAnswered).length;
    const minutesSpent = startedAt
      ? Math.round((Date.now() - startedAt) / 60000)
      : 0;
    // API format: answers = { [question_number]: option_id } (integers only, so
    // unanswered questions are left out); writing_answers = { [question_number]: text }
    // and must only use the writing question numbers (e.g. 49, 50).
    const answers = {};
    const writing_answers = {};
    questions.forEach((q, index) => {
      const key = q.question_number ?? index + 1;
      if (isWriting(q)) {
        writing_answers[key] = (writingAnswers[q.id] || "")
          .trim()
          .slice(0, WRITING_MAX_LENGTH);
      } else {
        const optionId = selectedOption(q.id)?.id;
        if (Number.isInteger(optionId)) answers[key] = optionId;
      }
    });
    return {
      attempted_questions: attempted,
      unsolved_questions: questions.length - attempted,
      time_spent: String(
        set?.time ? Math.min(minutesSpent, set.time) : minutesSpent
      ),
      correct_answers: correct.length,
      score: correct.reduce((sum, q) => sum + marksOf(q), 0),
      answers,
      writing_answers,
    };
  };

  const finishTest = () => {
    if (submitting || result) return;
    setConfirmSubmit(false);
    setSubmitError("");
    setSubmitting(true);
    localStorage.removeItem("testTime");
    const payload = buildPayload();
    submitSetResult(id, setId, payload, token)
      .then((data) => {
        setResult(data || {});
        dispatch(
          setSaveResult({
            ...(data || {}),
            time_spent: data?.time_spent ?? payload.time_spent,
            lkwb: {
              backLink,
              resultsLink: `/dashboard/lkwb-test/${id}/sets/${setId}/results`,
            },
          })
        );
        navigate("/dashboard/dashboard-results", { replace: true });
      })
      .catch((err) =>
        setSubmitError(errorMessage(err, "Unable to submit your test."))
      )
      .finally(() => setSubmitting(false));
  };

  if (isLoading) {
    return (
      <div className="dashboard-contents bigLoading loading__mode">
        <Loader label="Fetching all the questions. Please wait!" />
      </div>
    );
  }

  const question = questions[current];
  const questionNo = question?.question_number ?? current + 1;

  // Group sidebar numbers by scoring section, keeping API order.
  const groups = [];
  questions.forEach((q, index) => {
    const key = sectionOf(q);
    let group = groups.find((g) => g.key === key);
    if (!group) groups.push((group = { key, items: [] }));
    group.items.push({ question: q, index });
  });

  return (
    <>
      <div className="closeTest">
        <h3>{set?.title || "LKWB Test"}</h3>
        <Link to={backLink} className="close">
          <i className="bi bi-x-lg"></i>
        </Link>
      </div>

      {error ? (
        <div className="youhavenot-container">
          <p>{error}</p>
        </div>
      ) : (
        <Row>
          <Col md={9}>
            <div className="timer">
              <div className="d-flex align-items-center justify-content-between">
                <span>
                  Question No: {current + 1} / {questions.length}
                </span>
                {set?.time > 0 && !result && !submitting && !submitError && (
                  <span>
                    <i className="bi bi-stopwatch"></i>
                    <Timer
                      time={set.time}
                      testStatus={!result && !submitting}
                      handeChangeTimer={() => {}}
                      endTest={finishTest}
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="testQuestions lkwb-questions">
              {isWriting(question) ? (
                <WritingQuestion
                  question={question}
                  questionNo={questionNo}
                  value={writingAnswers[question.id] || ""}
                  onChange={(text) =>
                    setWritingAnswers((prev) => ({
                      ...prev,
                      [question.id]: text,
                    }))
                  }
                />
              ) : (
                <UBTQuestion question={question} questionNo={questionNo} />
              )}
              <div className="d-flex align-items-center justify-content-between">
                <button
                  className="btn hideonmobile"
                  onClick={() => setConfirmSubmit(true)}
                >
                  Submit Now
                </button>
                <div className="cta__btns text-end mt-2">
                  {current > 0 && (
                    <button
                      className="btn btn-outline"
                      onClick={() => setCurrent(current - 1)}
                    >
                      <i className="bi bi-chevron-left"></i>
                      Previous
                    </button>
                  )}
                  {current < questions.length - 1 && (
                    <button
                      className="btn"
                      onClick={() => setCurrent(current + 1)}
                    >
                      Next <i className="bi bi-chevron-right"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Col>
          <Col md={3} className="rightsidebar">
            <div className="sticky">
              <button
                className="btn fw showonmobile"
                onClick={() => setConfirmSubmit(true)}
              >
                Submit Now
              </button>
              <Accordion
                defaultActiveKey={groups.map((g) => g.key)}
                alwaysOpen
              >
                {groups.map((g) => (
                  <QuestionGroup
                    key={g.key}
                    eventId={g.key}
                    label={sectionLabel(g.key)}
                    items={g.items}
                    current={current}
                    isAnswered={isAnswered}
                    onSelect={setCurrent}
                  />
                ))}
              </Accordion>
            </div>
          </Col>
        </Row>
      )}

      <Modal show={confirmSubmit} onHide={() => setConfirmSubmit(false)}>
        <Modal.Body>
          <p>Do you really want to submit the test? You can't undo it.</p>
          <button className="btn btn-small" onClick={finishTest}>
            Yes
          </button>
          <button
            className="btn btn-small btn-secondary"
            onClick={() => setConfirmSubmit(false)}
          >
            No
          </button>
        </Modal.Body>
      </Modal>

      <Modal show={submitting || !!submitError} backdrop="static">
        <Modal.Body>
          {submitting ? (
            <div className="loading__mode bigloading">
              <Loader label="Submitting your test. Please wait!" />
            </div>
          ) : (
            <>
              <p>{submitError}</p>
              <button className="btn btn-small" onClick={finishTest}>
                Try Again
              </button>
              <button
                className="btn btn-small btn-secondary"
                onClick={() => setSubmitError("")}
              >
                Close
              </button>
            </>
          )}
        </Modal.Body>
      </Modal>

    </>
  );
};

export default LKWBTestQuestions;
